import React, { Suspense } from 'react';
import { InfinityLoader } from './components/InfinityLoader';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import ImpersonationBanner from './components/ImpersonationBanner';
import { preloadFilters } from './lib/filterUtils';

// Pre-warm filter cache
preloadFilters();

// ─── EAGER: Critical path (landing + auth + home) ───
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import HomePage from './pages/HomePage';

// ─── LAZY: Everything else code-split ───
const lazy = (fn) => React.lazy(fn);

const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const MemoriesHub = lazy(() => import('./pages/MemoriesHub'));
const CreateStudio = lazy(() => import('./pages/CreateStudio'));
const CreationStudio = lazy(() => import('./pages/CreationStudio'));
const LifeStoriesHub = lazy(() => import('./pages/LifeStoriesHub'));
const AICompanionHub = lazy(() => import('./pages/AICompanionHub'));
const ProfileHub = lazy(() => import('./pages/ProfileHub'));
const About = lazy(() => import('./pages/About'));
const MemoryDetail = lazy(() => import('./pages/MemoryDetail'));
const LifeChapterDetail = lazy(() => import('./pages/LifeChapterDetail'));
const LifeChaptersPage = lazy(() => import('./pages/LifeChaptersPage'));
const LifeTimeline = lazy(() => import('./pages/LifeTimeline'));
const InfinityTimeline = lazy(() => import('./pages/InfinityTimeline'));
const LifeMap = lazy(() => import('./pages/LifeMap'));
const People = lazy(() => import('./pages/People'));
const PersonProfile = lazy(() => import('./pages/PersonProfile'));
const UnknownFacesReview = lazy(() => import('./pages/UnknownFacesReview'));
const CameraOS = lazy(() => import('./pages/CameraOS'));
const PhotoEditor = lazy(() => import('./pages/PhotoEditor'));
const MemoryFilter = lazy(() => import('./pages/MemoryFilter'));
const OnThisDay = lazy(() => import('./pages/OnThisDay'));
const MemoryMap = lazy(() => import('./pages/MemoryMap'));
const LocationStoryPage = lazy(() => import('./pages/LocationStoryPage'));
const TravelJournalPage = lazy(() => import('./pages/TravelJournalPage'));
const StoryGenerator = lazy(() => import('./pages/StoryGenerator'));
const SharedStory = lazy(() => import('./pages/SharedStory'));
const BookCreate = lazy(() => import('./pages/BookCreate'));
const BookOrder = lazy(() => import('./pages/BookOrder'));
const BookProofExport = lazy(() => import('./pages/BookProofExport'));
const Books = lazy(() => import('./pages/Books'));
const PrintOrders = lazy(() => import('./pages/PrintOrders'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const ProjectCreate = lazy(() => import('./pages/ProjectCreate'));
const Documentaries = lazy(() => import('./pages/Documentaries'));
const DocumentaryDetail = lazy(() => import('./pages/DocumentaryDetail'));
const SharedDocumentary = lazy(() => import('./pages/SharedDocumentary'));
const SharedMemory = lazy(() => import('./pages/SharedMemory'));
const SharedLifeMovie = lazy(() => import('./pages/SharedLifeMovie'));
const SharedTrailerPage = lazy(() => import('./pages/SharedTrailerPage'));
const LifeMovie = lazy(() => import('./pages/LifeMovie'));
const LifeMovieCreate = lazy(() => import('./pages/LifeMovieCreate'));
const LifeGraph = lazy(() => import('./pages/LifeGraph'));
const MemoryExplorer = lazy(() => import('./pages/MemoryExplorer'));
const VoiceMemories = lazy(() => import('./pages/VoiceMemories'));
const Uploads = lazy(() => import('./pages/Uploads'));
const Settings = lazy(() => import('./pages/Settings'));
const YearlyReport = lazy(() => import('./pages/YearlyReport'));
const ChapterDetail = lazy(() => import('./pages/ChapterDetail'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const WeeklyRecapPage = lazy(() => import('./pages/WeeklyRecapPage'));
const RelationshipGraphPage = lazy(() => import('./pages/RelationshipGraphPage'));
const LifeStoriesFeed = lazy(() => import('./pages/LifeStoriesFeed'));
const FeedPostDetail = lazy(() => import('./pages/FeedPostDetail'));
const LifeStoriesApp = lazy(() => import('./pages/LifeStoriesApp'));
const StoryDetailApp = lazy(() => import('./pages/StoryDetailApp'));
const LifeCircleDetail = lazy(() => import('./pages/LifeCircleDetail'));
const LifeStartDashboard = lazy(() => import('./pages/LifeStartDashboard'));
const ChildTimeline = lazy(() => import('./pages/ChildTimeline'));
const MomentDetailPage = lazy(() => import('./pages/MomentDetailPage'));
const OnboardingFlow = lazy(() => import('./pages/OnboardingFlow'));
const LivingLegaciesPage = lazy(() => import('./pages/LivingLegaciesPage'));
const AlbumsPage = lazy(() => import('./pages/AlbumsPage'));
const AlbumDetail = lazy(() => import('./pages/AlbumDetail'));
const PublicAlbumUpload = lazy(() => import('./pages/PublicAlbumUpload'));
const OrganizationDashboard = lazy(() => import('./pages/OrganizationDashboard'));

// Admin (always lazy — separate chunk)
import { AdminAuthProvider } from './admin/contexts/AdminAuthContext';
import { AdminProtectedRoute } from './admin/components/AdminLayout';
import AdminErrorBoundary from './admin/components/AdminErrorBoundary';
const AdminLogin = lazy(() => import('./admin/pages/AdminLogin'));
const ChangePassword = lazy(() => import('./admin/pages/ChangePassword'));
const MissionControl = lazy(() => import('./admin/pages/MissionControl'));
const ExecutiveOverview = lazy(() => import('./admin/pages/ExecutiveOverview'));
const GrowthAnalytics = lazy(() => import('./admin/pages/GrowthAnalytics'));
const UserBehavior = lazy(() => import('./admin/pages/UserBehavior'));
const BookAnalytics = lazy(() => import('./admin/pages/BookAnalytics'));
const Operations = lazy(() => import('./admin/pages/Operations'));
const AdminDiagnostics = lazy(() => import('./admin/pages/AdminDiagnostics'));
const Alerts = lazy(() => import('./admin/pages/Alerts'));
const AdminUsers = lazy(() => import('./admin/pages/AdminUsers'));
const PricingManagement = lazy(() => import('./admin/pages/PricingManagement'));
const ProductPricingManagement = lazy(() => import('./admin/pages/ProductPricingManagement'));
const AdminUserManagement = lazy(() => import('./admin/pages/AdminUserManagement'));
const AdminActivityDashboard = lazy(() => import('./admin/pages/AdminActivityDashboard'));
const ContentModeration = lazy(() => import('./admin/pages/ContentModeration'));
const EverBookNews = lazy(() => import('./admin/pages/EverBookNews'));
const DiscoverContent = lazy(() => import('./admin/pages/DiscoverContent'));
const SharedLegacyPage = lazy(() => import('./pages/SharedLegacyPage'));
const FamilyTimelines = lazy(() => import('./pages/FamilyTimelines'));
const VideoTrimPage = lazy(() => import('./pages/VideoTrimPage'));
const ExplorePage = lazy(() => import('./pages/ExplorePage'));
const TrailerStudioPage = lazy(() => import('./pages/TrailerStudioPage'));

// Placeholder admin pages
const Revenue = lazy(() => import('./admin/pages/PlaceholderPages').then(m => ({ default: m.Revenue })));
const Marketing = lazy(() => import('./admin/pages/PlaceholderPages').then(m => ({ default: m.Marketing })));
const Retention = lazy(() => import('./admin/pages/PlaceholderPages').then(m => ({ default: m.Retention })));
const AIHealth = lazy(() => import('./admin/pages/PlaceholderPages').then(m => ({ default: m.AIHealth })));
const Support = lazy(() => import('./admin/pages/PlaceholderPages').then(m => ({ default: m.Support })));
const AdminSettings = lazy(() => import('./admin/pages/PlaceholderPages').then(m => ({ default: m.AdminSettings })));

const adminRoute = (element) => (
  <AdminErrorBoundary>
    <AdminAuthProvider>
      {element}
    </AdminAuthProvider>
  </AdminErrorBoundary>
);

// Route-level loading fallback
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
    <InfinityLoader size={120} label="Loading..." />
  </div>
);

const ProtectedRoute = ({ children, skipOnboardingCheck = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <InfinityLoader size={120} label="Loading..." />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!skipOnboardingCheck && localStorage.getItem('needs_onboarding') === 'true') {
    return <Navigate to="/onboarding" />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <InfinityLoader size={120} />
      </div>
    );
  }
  
  if (user) return <Navigate to="/memories" />;
  return children;
};

// Smart route for Life Stories public feed
const LifeStoriesRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]"><InfinityLoader size={96} /></div>;
  return user ? <LifeStoriesApp /> : <LifeStoriesFeed />;
};

const StoryDetailRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]"><InfinityLoader size={96} /></div>;
  return user ? <StoryDetailApp /> : <FeedPostDetail />;
};

function App() {
  return (
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
    <AuthProvider>
        <Router>
          <Suspense fallback={<RouteFallback />}>
          <ImpersonationBanner />
          <Routes>
            {/* ============ PUBLIC ROUTES ============ */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/onboarding" element={<ProtectedRoute skipOnboardingCheck><OnboardingFlow /></ProtectedRoute>} />
            
            {/* Public Shared Content */}
            <Route path="/shared/documentary/:shareToken" element={<SharedDocumentary />} />
            <Route path="/memory/:slug" element={<SharedMemory />} />
            <Route path="/life-movie/watch/:shareToken" element={<SharedLifeMovie />} />
            <Route path="/shared-story/:token" element={<SharedStory />} />
            <Route path="/year/:year/:username" element={<YearlyReport />} />
            <Route path="/t/:shareSlug" element={<SharedTrailerPage />} />
            <Route path="/legacy/:slug" element={<SharedLegacyPage />} />
            
            {/* Life Stories Feed - Smart routing */}
            <Route path="/stories" element={<ProtectedRoute><LifeStoriesHub /></ProtectedRoute>} />
            <Route path="/stories/:id" element={<StoryDetailRoute />} />
            <Route path="/story-wall" element={<LifeStoriesRoute />} />
            <Route path="/story-wall/:id" element={<StoryDetailRoute />} />
            <Route path="/feed" element={<Navigate to="/story-wall" />} />
            <Route path="/feed/:id" element={<StoryDetailRoute />} />

            {/* ============ 5 PRIMARY SECTIONS ============ */}
            <Route path="/memories" element={<ProtectedRoute><MemoriesHub /></ProtectedRoute>} />
            <Route path="/explore" element={<ProtectedRoute><ExplorePage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfileHub /></ProtectedRoute>} />
            <Route path="/organization" element={<ProtectedRoute><OrganizationDashboard /></ProtectedRoute>} />
            
            {/* Home redirects to Memories (the new default) */}
            <Route path="/home" element={<Navigate to="/memories" />} />
            
            {/* Tools accessible from Explore */}
            <Route path="/create" element={<ProtectedRoute><CreateStudio /></ProtectedRoute>} />
            <Route path="/creation-studio" element={<ProtectedRoute><CreationStudio /></ProtectedRoute>} />
            <Route path="/ai-companion" element={<ProtectedRoute><AICompanionHub /></ProtectedRoute>} />
            
            {/* ============ SUB-PAGES & DETAIL ROUTES ============ */}
            {/* Memory detail */}
            <Route path="/memories/:id" element={<ProtectedRoute><MemoryDetail /></ProtectedRoute>} />
            
            {/* Life Chapters */}
            <Route path="/life-chapters" element={<ProtectedRoute><LifeChaptersPage /></ProtectedRoute>} />
            <Route path="/life-chapters/:id" element={<ProtectedRoute><LifeChapterDetail /></ProtectedRoute>} />
            
            {/* Life Circles */}
            <Route path="/life-circles/:id" element={<ProtectedRoute><LifeCircleDetail /></ProtectedRoute>} />
            
            {/* Moments */}
            <Route path="/moments/:id" element={<ProtectedRoute><MomentDetailPage /></ProtectedRoute>} />
            
            {/* Timeline (full-page view) */}
            <Route path="/life-timeline" element={<ProtectedRoute><LifeTimeline /></ProtectedRoute>} />
            
            {/* People */}
            <Route path="/people" element={<ProtectedRoute><People /></ProtectedRoute>} />
            <Route path="/people/:id" element={<ProtectedRoute><PersonProfile /></ProtectedRoute>} />
            <Route path="/people/faces/review" element={<ProtectedRoute><UnknownFacesReview /></ProtectedRoute>} />
            
            {/* Camera */}
            <Route path="/camera" element={<ProtectedRoute><CameraOS /></ProtectedRoute>} />
            <Route path="/edit/:captureId" element={<ProtectedRoute><PhotoEditor /></ProtectedRoute>} />
            <Route path="/memories/:memoryId/filter" element={<ProtectedRoute><MemoryFilter /></ProtectedRoute>} />
            
            {/* LifeStart (Child Timeline) */}
            <Route path="/lifestart" element={<ProtectedRoute><LifeStartDashboard /></ProtectedRoute>} />
            <Route path="/lifestart/:childId" element={<ProtectedRoute><ChildTimeline /></ProtectedRoute>} />
            
            {/* On This Day (full page) */}
            <Route path="/on-this-day" element={<ProtectedRoute><OnThisDay /></ProtectedRoute>} />
            
            {/* Memory Map */}
            <Route path="/memory-map" element={<ProtectedRoute><MemoryMap /></ProtectedRoute>} />
            <Route path="/location-story/:clusterId" element={<ProtectedRoute><LocationStoryPage /></ProtectedRoute>} />
            <Route path="/travel-journal" element={<ProtectedRoute><TravelJournalPage /></ProtectedRoute>} />
            
            {/* Story Generator */}
            <Route path="/story-generator" element={<ProtectedRoute><StoryGenerator /></ProtectedRoute>} />
            
            {/* Books */}
            <Route path="/books" element={<ProtectedRoute><Books /></ProtectedRoute>} />
            <Route path="/books/create" element={<ProtectedRoute><BookCreate /></ProtectedRoute>} />
            <Route path="/books/proof" element={<ProtectedRoute><BookProofExport /></ProtectedRoute>} />
            <Route path="/books/proof/:bookId" element={<ProtectedRoute><BookProofExport /></ProtectedRoute>} />
            <Route path="/books/:bookId/proof" element={<ProtectedRoute><BookProofExport /></ProtectedRoute>} />
            <Route path="/books/:bookId/order" element={<ProtectedRoute><BookOrder /></ProtectedRoute>} />
            <Route path="/print-orders" element={<ProtectedRoute><PrintOrders /></ProtectedRoute>} />
            
            {/* Documentaries & Movies */}
            <Route path="/documentaries" element={<ProtectedRoute><Documentaries /></ProtectedRoute>} />
            <Route path="/documentaries/:id" element={<ProtectedRoute><DocumentaryDetail /></ProtectedRoute>} />
            <Route path="/family-timelines" element={<ProtectedRoute><FamilyTimelines /></ProtectedRoute>} />
            <Route path="/family-timelines/:id" element={<ProtectedRoute><FamilyTimelines /></ProtectedRoute>} />
            <Route path="/video-trim/:memoryId" element={<ProtectedRoute><VideoTrimPage /></ProtectedRoute>} />
            <Route path="/life-movie" element={<ProtectedRoute><LifeMovie /></ProtectedRoute>} />
            <Route path="/life-movie/create" element={<ProtectedRoute><LifeMovieCreate /></ProtectedRoute>} />
            <Route path="/life-movie/:movieId" element={<ProtectedRoute><LifeMovie /></ProtectedRoute>} />
            <Route path="/trailer-studio" element={<ProtectedRoute><TrailerStudioPage /></ProtectedRoute>} />
            
            {/* Advanced tools (accessible from Profile > Advanced) */}
            <Route path="/life-graph" element={<ProtectedRoute><LifeGraph /></ProtectedRoute>} />
            <Route path="/explorer" element={<ProtectedRoute><MemoryExplorer /></ProtectedRoute>} />
            <Route path="/voice-memories" element={<ProtectedRoute><VoiceMemories /></ProtectedRoute>} />
            <Route path="/uploads" element={<ProtectedRoute><Uploads /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
            <Route path="/billing/success" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
            <Route path="/weekly-recap" element={<ProtectedRoute><WeeklyRecapPage /></ProtectedRoute>} />
            <Route path="/relationship-graph" element={<ProtectedRoute><RelationshipGraphPage /></ProtectedRoute>} />
            
            {/* Chapter detail (legacy) */}
            <Route path="/events/:id" element={<ProtectedRoute><ChapterDetail /></ProtectedRoute>} />
            <Route path="/chapters/:id" element={<ProtectedRoute><ChapterDetail /></ProtectedRoute>} />
            
            {/* ============ LEGACY REDIRECTS ============ */}
            <Route path="/dashboard" element={<Navigate to="/memories" />} />
            <Route path="/my-memories" element={<Navigate to="/memories?tab=library" />} />
            <Route path="/timeline" element={<ProtectedRoute><InfinityTimeline /></ProtectedRoute>} />
            <Route path="/life-map" element={<ProtectedRoute><LifeMap /></ProtectedRoute>} />
            <Route path="/photos" element={<Navigate to="/memories?tab=library" />} />
            <Route path="/gallery" element={<Navigate to="/memories?tab=library" />} />
            {/* Albums */}
            <Route path="/albums" element={<ProtectedRoute><AlbumsPage /></ProtectedRoute>} />
            <Route path="/albums/:albumId" element={<ProtectedRoute><AlbumDetail /></ProtectedRoute>} />
            <Route path="/a/:slug" element={<PublicAlbumUpload />} />

            <Route path="/places" element={<Navigate to="/memory-map" />} />
            <Route path="/projects/new" element={<ProtectedRoute><ProjectCreate /></ProtectedRoute>} />
            <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute><ProjectCreate /></ProtectedRoute>} />
            <Route path="/discovery" element={<Navigate to="/explore" />} />
            <Route path="/creation/wizard" element={<Navigate to="/create" />} />
            <Route path="/creation/success" element={<Navigate to="/memories" />} />
            <Route path="/collaborators" element={<Navigate to="/memories" />} />
            <Route path="/life-stories" element={<Navigate to="/story-wall" />} />
            <Route path="/life-stories/:id" element={<StoryDetailRoute />} />
            <Route path="/life-story" element={<Navigate to="/stories" />} />
            <Route path="/life-story/new" element={<Navigate to="/create?type=story" />} />
            <Route path="/life-story/:id" element={<ProtectedRoute><LifeChapterDetail /></ProtectedRoute>} />
            <Route path="/family-archive" element={<Navigate to="/stories" />} />
            <Route path="/legacy-vault" element={<Navigate to="/legacies" />} />
            <Route path="/legacies" element={<ProtectedRoute><LivingLegaciesPage /></ProtectedRoute>} />
            
            {/* ============ ADMIN PLATFORM ============ */}
            <Route path="/admin/login" element={adminRoute(<AdminLogin />)} />
            <Route path="/admin/change-password" element={adminRoute(<AdminProtectedRoute><ChangePassword /></AdminProtectedRoute>)} />
            <Route path="/admin" element={adminRoute(<AdminProtectedRoute><MissionControl /></AdminProtectedRoute>)} />
            <Route path="/admin/executive" element={adminRoute(<AdminProtectedRoute permission="view_all_dashboards"><ExecutiveOverview /></AdminProtectedRoute>)} />
            <Route path="/admin/growth" element={adminRoute(<AdminProtectedRoute permission="view_growth_analytics"><GrowthAnalytics /></AdminProtectedRoute>)} />
            <Route path="/admin/users" element={adminRoute(<AdminProtectedRoute permission="view_user_activity"><UserBehavior /></AdminProtectedRoute>)} />
            <Route path="/admin/user-management" element={adminRoute(<AdminProtectedRoute permission="view_user_activity"><AdminUserManagement /></AdminProtectedRoute>)} />
            <Route path="/admin/activity" element={adminRoute(<AdminProtectedRoute permission="view_all_dashboards"><AdminActivityDashboard /></AdminProtectedRoute>)} />
            <Route path="/admin/books" element={adminRoute(<AdminProtectedRoute permission="view_book_pipeline"><BookAnalytics /></AdminProtectedRoute>)} />
            <Route path="/admin/revenue" element={adminRoute(<AdminProtectedRoute permission="view_revenue_data"><Revenue /></AdminProtectedRoute>)} />
            <Route path="/admin/pricing" element={adminRoute(<AdminProtectedRoute permission="view_revenue_data"><PricingManagement /></AdminProtectedRoute>)} />
            <Route path="/admin/product-pricing" element={adminRoute(<AdminProtectedRoute permission="view_product_pricing"><ProductPricingManagement /></AdminProtectedRoute>)} />
            <Route path="/admin/marketing" element={adminRoute(<AdminProtectedRoute permission="view_user_acquisition"><Marketing /></AdminProtectedRoute>)} />
            <Route path="/admin/retention" element={adminRoute(<AdminProtectedRoute permission="view_retention"><Retention /></AdminProtectedRoute>)} />
            <Route path="/admin/operations" element={adminRoute(<AdminProtectedRoute permission="view_operational_metrics"><Operations /></AdminProtectedRoute>)} />
            <Route path="/admin/diagnostics" element={adminRoute(<AdminProtectedRoute><AdminDiagnostics /></AdminProtectedRoute>)} />
            <Route path="/admin/ai-health" element={adminRoute(<AdminProtectedRoute permission="view_ai_status"><AIHealth /></AdminProtectedRoute>)} />
            <Route path="/admin/support" element={adminRoute(<AdminProtectedRoute permission="view_support_tickets"><Support /></AdminProtectedRoute>)} />
            <Route path="/admin/content-moderation" element={adminRoute(<AdminProtectedRoute permission="view_support_tickets"><ContentModeration /></AdminProtectedRoute>)} />
            <Route path="/admin/discover-content" element={adminRoute(<AdminProtectedRoute permission="manage_discover_content"><DiscoverContent /></AdminProtectedRoute>)} />
            <Route path="/admin/everbook-news" element={adminRoute(<AdminProtectedRoute permission="manage_everbook_news"><EverBookNews /></AdminProtectedRoute>)} />
            <Route path="/admin/alerts" element={adminRoute(<AdminProtectedRoute permission="view_system_alerts"><Alerts /></AdminProtectedRoute>)} />
            <Route path="/admin/admin-users" element={adminRoute(<AdminProtectedRoute permission="manage_admin_users"><AdminUsers /></AdminProtectedRoute>)} />
            <Route path="/admin/settings" element={adminRoute(<AdminProtectedRoute permission="configure_settings"><AdminSettings /></AdminProtectedRoute>)} />
            
            {/* Catch all */}
            <Route path="/about" element={<About />} />
            <Route path="*" element={<Navigate to="/memories" />} />
          </Routes>
          </Suspense>
        </Router>
        <Toaster richColors position="top-right" />
    </AuthProvider>
    </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
